import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_BADGE, TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";

export default function WebsiteBranches() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/branches",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Branches");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Branches" />
            <TabHeader
                className="px-3 pt-3"
                title="Branch cards"
                highlights={["Each row is one branch on the home page, the Contact Us page, the About map, and the footer"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { is_active: true } })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/branches/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Branch deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_active === true || row.is_active === 1;
    const meta = [row.label, row.address].filter(Boolean).join(" ? ");
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.name || "Untitled"}</span>
                {meta ? <span className={`${TEXT_BADGE} white-space-nowrap overflow-hidden text-overflow-ellipsis`}>{meta}</span> : null}
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/branches",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: {
                ...record,
                name: record.name || "",
                is_active: record.is_active === true || record.is_active === 1,
            },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Branch saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a branch" : "Edit branch"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="name" className="w-full" value={record?.name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="name">Branch name</label>
                    </FloatLabel>
                    <FieldNote text="Required. The heading on the card, such as Karelibaug." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="label" className="w-full" value={record?.label ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, label: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="label">Short label</label>
                    </FloatLabel>
                    <FieldNote text="The small badge, such as Branch 01. Leave blank to hide it on the Contact Us card." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={3} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Short description</label>
                    </FloatLabel>
                    <FieldNote text="Shown under the name on the Contact Us card. The home card does not show this." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="address" className="w-full" rows={3} autoResize value={record?.address || ""} onChange={(event) => setRecord((prev) => ({ ...prev, address: event.target.value }))} disabled={saving} />
                        <label htmlFor="address">Address</label>
                    </FloatLabel>
                    <FieldNote text="Shown on the home card, the Contact Us card, the About map, and the footer." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="phone" className="w-full" value={record?.phone ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, phone: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="phone">Phone</label>
                    </FloatLabel>
                    <FieldNote text="Adds the Call button. Example: +919265347133. Leave blank to hide Call." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="whatsapp" className="w-full" value={record?.whatsapp ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, whatsapp: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="whatsapp">WhatsApp link</label>
                    </FloatLabel>
                    <FieldNote text="Used on the About page map. Example: https://wa.me/919265347133. Leave blank to hide it." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="map_url" className="w-full" value={record?.map_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, map_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="map_url">Directions link</label>
                    </FloatLabel>
                    <FieldNote text="The Directions button. Paste a Google Maps link. Leave blank and the site builds one from the address." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="map_query" className="w-full" value={record?.map_query ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, map_query: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="map_query">Words used to place the map</label>
                    </FloatLabel>
                    <FieldNote text="Example: SAHAS Institute of Commerce, Karelibaug, Vadodara. Leave blank and the site uses the address. The map fills the top of the Contact card." />
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_active" checked={record?.is_active === true || record?.is_active === 1} onChange={(event) => setRecord((prev) => ({ ...prev, is_active: event.checked }))} disabled={saving} />
                    <label htmlFor="is_active" className={TEXT_SMALL}>Show this branch on the website</label>
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
